import { useCallback, useEffect, useMemo, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { cropImageToPng, createImage } from '@/utils/cropImageToPng';
import {
  KeenIcon,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from '@/components';

interface ImageCropperModalProps {
  open: boolean;
  file: File | null;
  aspect: number;
  minWidth: number;
  minHeight: number;
  title: string;
  onClose: () => void;
  onConfirm: (croppedFile: File) => void;
}

const ImageCropperModal = ({
  open,
  file,
  aspect,
  minWidth,
  minHeight,
  title,
  onClose,
  onConfirm,
}: ImageCropperModalProps) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [originalResolution, setOriginalResolution] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [error, setError] = useState<string>('');
  const [maxZoom, setMaxZoom] = useState(6);

  const isCropBelowMinimum = useMemo(() => {
    if (!croppedAreaPixels) return true;

    // We allow all crops now as we scale up low-resolution images
    return false;
  }, [croppedAreaPixels]);

  useEffect(() => {
    if (!open || !file) return;

    const objectUrl = URL.createObjectURL(file);
    setImageSrc(objectUrl);

    (async () => {
      try {
        const img = await createImage(objectUrl);
        setOriginalResolution({ width: img.width, height: img.height });

        // Calculate maxZoom to allow user to zoom in
        // We set a high max zoom (e.g. 10) to allow flexibility
        setMaxZoom(10);
        setError('');
      } catch {
        setError('Failed to load image.');
      }
    })();

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file, open]);

  useEffect(() => {
    if (!open) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setError('');
  }, [open]);

  const handleCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
    setError('');
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!file || !imageSrc || !croppedAreaPixels) return;

    const blob = await cropImageToPng({
      imageSrc,
      pixelCrop: croppedAreaPixels,
      rotation,
      targetWidth: minWidth,
      targetHeight: minHeight,
    });

    const croppedFile = new File(
      [blob],
      `${file.name.replace(/\.[^/.]+$/, '')}.png`,
      {
        type: 'image/png',
      }
    );

    onConfirm(croppedFile);
  }, [
    croppedAreaPixels,
    file,
    imageSrc,
    minHeight,
    minWidth,
    onConfirm,
    rotation,
  ]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="image-cropper-modal-title"
      disableEnforceFocus={false}
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
    >
      <ModalContent className="bg-white dark:bg-gray-100 contrast:bg-gray-100 rounded-xl shadow-xl w-full max-w-[900px] max-h-[90vh] flex flex-col overflow-hidden">
        <ModalHeader className="border-b border-gray-200 dark:border-gray-700 contrast:border-gray-700 px-6 py-4 flex-shrink-0">
          <ModalTitle
            id="image-cropper-modal-title"
            className="text-xl font-bold text-gray-900 dark:text-white contrast:text-white"
          >
            {title}
          </ModalTitle>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-800 contrast:text-gray-800 hover:text-gray-700 dark:hover:text-gray-800 contrast:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-1"
            aria-label="Close"
            type="button"
          >
            <KeenIcon icon="cross" className="text-xl" />
          </button>
        </ModalHeader>

        <ModalBody className="p-6 overflow-y-auto flex-grow custom-scrollbar">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              <div className="text-sm text-gray-700 dark:text-gray-800 contrast:text-gray-800">
                Minimum Resolution: {minWidth}×{minHeight}px. Aspect ratio is
                locked.
              </div>
              {originalResolution && (
                <div className="text-sm font-medium text-gray-600 dark:text-gray-700">
                  Original Resolution: {originalResolution.width}×
                  {originalResolution.height}px
                </div>
              )}
            </div>

            <div className="relative w-full h-[380px] bg-gray-100 rounded-lg overflow-hidden">
              {imageSrc && (
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={aspect}
                  onCropChange={setCrop}
                  onRotationChange={setRotation}
                  onZoomChange={setZoom}
                  onCropComplete={handleCropComplete}
                  restrictPosition={true}
                  minZoom={1}
                  maxZoom={maxZoom}
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="image-cropper-zoom"
                  className="form-label text-gray-700 dark:text-gray-800 contrast:text-gray-800"
                >
                  Zoom
                </label>
                <input
                  id="image-cropper-zoom"
                  type="range"
                  min={1}
                  max={maxZoom}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="range range-primary w-full bg-gray-200 dark:bg-gray-300 contrast:bg-gray-300"
                  disabled={!!error && !croppedAreaPixels}
                  autoComplete="off"
                />
              </div>
              <div>
                <label
                  htmlFor="image-cropper-rotation"
                  className="form-label text-gray-700 dark:text-gray-800 contrast:text-gray-800"
                >
                  Rotation
                </label>
                <input
                  id="image-cropper-rotation"
                  type="range"
                  min={0}
                  max={360}
                  step={1}
                  value={rotation}
                  onChange={(e) => setRotation(Number(e.target.value))}
                  className="range range-primary w-full bg-gray-200 dark:bg-gray-300 contrast:bg-gray-300"
                  disabled={!!error && !croppedAreaPixels}
                  autoComplete="off"
                />
              </div>
            </div>

            {error && (
              <div
                className="text-danger contrast:text-red-500 text-sm"
                role="alert"
              >
                {error}
              </div>
            )}
          </div>
        </ModalBody>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 contrast:border-gray-700 flex-shrink-0">
          <button type="button" onClick={onClose} className="btn btn-light">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="btn btn-primary"
            disabled={!file || !imageSrc || isCropBelowMinimum || !!error}
          >
            Save crop
          </button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ImageCropperModal;
